/**
 * External dependencies
 */
import { DateTime } from 'luxon';
import type { ReactNode } from 'react';

/**
 * Internal dependencies
 */
import { Card, CardContent } from '@Components/Card';

export const COLORS = {
  sent: '#3b82f6',
  received: '#10b981',
  recipients: '#f59e0b',
  flow: '#8b5cf6',
  campaign: '#ec4899',
  other: '#a1a1aa',
  afterHours: '#d4d4d8',
  grid: '#e4e4e7',
  axis: '#71717a',
};

/** Per-Flow series colours, in `flowSeries` order; picked to stay clear of the source colours above. */
export const FLOW_SERIES_COLORS = [
  '#8b5cf6',
  '#06b6d4',
  '#f97316',
  '#84cc16',
  '#eab308',
  '#14b8a6',
  '#6366f1',
  '#b45309',
];
export const OTHER_FLOWS_COLOR = '#a78bfa';
export const UNATTRIBUTED_FLOW_COLOR = '#64748b';

export const AXIS_PROPS = {
  tick: { fontSize: 12 },
  stroke: COLORS.axis,
};

const MINUTE_SECONDS = 60;
const HOUR_SECONDS = 60 * MINUTE_SECONDS;
const DAY_SECONDS = 24 * HOUR_SECONDS;

export const formatDay = (date: string, format: string) =>
  DateTime.fromISO(date).toFormat(format);

export const formatHour = (hour: number) =>
  DateTime.fromObject({ hour }).toFormat('ha').toLowerCase();

export const formatShare = (share: number | null, digits = 0) =>
  share === null ? '—' : `${(share * 100).toFixed(digits)}%`;

export const formatCount = (value: number) => value.toLocaleString();

/** Relative time from an ISO timestamp, e.g. `2 days ago`. */
export const formatAgo = (timestamp: string) =>
  DateTime.fromISO(timestamp).toRelative() ?? '';

/** Compact duration: `45s`, `12m`, `3h 20m`, `2d 4h`. */
export const formatDuration = (seconds: number | null) => {
  if (seconds === null) return '—';
  if (seconds < MINUTE_SECONDS) return `${Math.round(seconds)}s`;
  if (seconds < HOUR_SECONDS) return `${Math.round(seconds / MINUTE_SECONDS)}m`;
  if (seconds < DAY_SECONDS) {
    const hours = Math.floor(seconds / HOUR_SECONDS);
    const minutes = Math.round((seconds % HOUR_SECONDS) / MINUTE_SECONDS);
    return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  const days = Math.floor(seconds / DAY_SECONDS);
  const hours = Math.round((seconds % DAY_SECONDS) / HOUR_SECONDS);
  return hours ? `${days}d ${hours}h` : `${days}d`;
};

export function StatCard({
  label,
  value,
  detail,
}: {
  label: string;
  value: ReactNode;
  detail?: ReactNode;
}) {
  return (
    <Card>
      <CardContent>
        <div className="text-sm text-zinc-500">{label}</div>
        <div className="text-3xl font-semibold">{value}</div>
        <div className="min-h-5 text-sm text-zinc-500">{detail}</div>
      </CardContent>
    </Card>
  );
}

export function ChartCard({
  title,
  action,
  children,
}: {
  title: string;
  /** Control shown at the end of the title row, e.g. a view toggle. */
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Card>
      <CardContent>
        <div className="mb-4 flex items-center justify-between gap-4">
          <div className="text-sm font-medium">{title}</div>
          {action}
        </div>
        {children}
      </CardContent>
    </Card>
  );
}
